/**
 * Derive open/closed status and display text from opening_hours object.
 * Hours format: "9:00 AM - 9:00 PM" or "Closed"
 */

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
/** Display order: Monday first */
export const DAY_KEYS_DISPLAY = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export function getCurrentDayKey() {
  const d = new Date().getDay();
  return DAY_KEYS[d];
}

export function getHoursForDay(openingHours, dayKey) {
  if (!openingHours || typeof openingHours !== 'object') return null;
  const raw = openingHours[dayKey];
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.toLowerCase() === 'closed') return { closed: true, text: trimmed };
  return { closed: false, text: trimmed };
}

/**
 * Parse "9:00 AM - 9:00 PM" into { open, close } or null.
 * Simple regex; does not handle all locales.
 */
function parseTimeRange(text) {
  if (!text || text.toLowerCase() === 'closed') return null;
  const match = text.match(/(\d{1,2}(?::\d{2})?\s*(?:AM|PM|am|pm))\s*[-–]\s*(\d{1,2}(?::\d{2})?\s*(?:AM|PM|am|pm))/i);
  if (!match) return null;
  return { open: match[1].trim(), close: match[2].trim() };
}

/**
 * Very rough "is now within this time range?" using string comparison.
 * For production, use a proper time library and timezone.
 */
function isNowWithinRange(openStr, closeStr) {
  try {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const current = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const toMinutes = (str) => {
      const m = str.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM|am|pm)/i);
      if (!m) return -1;
      let h = parseInt(m[1], 10);
      const min = m[2] ? parseInt(m[2], 10) : 0;
      if (/pm/i.test(m[3]) && h !== 12) h += 12;
      if (/am/i.test(m[3]) && h === 12) h = 0;
      return h * 60 + min;
    };
    const openM = toMinutes(openStr);
    const closeM = toMinutes(closeStr);
    const nowM = now.getHours() * 60 + now.getMinutes();
    if (openM < 0 || closeM < 0) return null;
    if (closeM < openM) return nowM >= openM || nowM < closeM;
    return nowM >= openM && nowM < closeM;
  } catch {
    return null;
  }
}

/**
 * Returns { status: 'open'|'closed', message, todayHours } for display.
 */
export function calculateStatus(openingHours) {
  const dayKey = getCurrentDayKey();
  const today = getHoursForDay(openingHours, dayKey);
  const todayHours = today ? today.text : null;

  if (!today || today.closed) {
    return {
      status: 'closed',
      message: todayHours || 'Closed',
      todayHours: todayHours || 'Closed',
      nextOpen: null
    };
  }

  const range = parseTimeRange(today.text);
  if (!range) {
    return {
      status: 'unknown',
      message: today.text,
      todayHours: today.text,
      nextOpen: null
    };
  }

  const isOpen = isNowWithinRange(range.open, range.close);
  if (isOpen === true) {
    return {
      status: 'open',
      message: range.close,
      todayHours: today.text,
      closesAt: range.close
    };
  }
  if (isOpen === false) {
    return {
      status: 'closed',
      message: today.text,
      todayHours: today.text,
      nextOpen: range.open
    };
  }
  return {
    status: 'unknown',
    message: today.text,
    todayHours: today.text,
    nextOpen: null
  };
}

export { DAY_KEYS };
